"use client"

import { useMemo, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Copy, ArrowRightLeft, AlertCircle, Check, Trash2 } from "lucide-react"
import { toast } from "sonner"
import yaml from "js-yaml"

type ConversionMode = "json-to-yaml" | "yaml-to-json"

export function JsonYamlConverter() {
  const [mode, setMode] = useState<ConversionMode>("json-to-yaml")
  const [input, setInput] = useState("")
  const [indentSize, setIndentSize] = useState(2)

  const { output, error } = useMemo(() => {
    if (!input.trim()) {
      return { output: "", error: null }
    }

    try {
      if (mode === "json-to-yaml") {
        const parsed: unknown = JSON.parse(input)
        return {
          output: yaml.dump(parsed, {
            indent: indentSize,
            lineWidth: -1,
            noRefs: true,
            sortKeys: false,
          }),
          error: null,
        }
      }

      const parsed: unknown = yaml.load(input)
      return { output: JSON.stringify(parsed, null, indentSize), error: null }
    } catch (conversionError) {
      return {
        output: "",
        error: conversionError instanceof Error ? conversionError.message : "Invalid input",
      }
    }
  }, [input, mode, indentSize])

  const swapContent = () => {
    if (output) {
      setInput(output)
      setMode(mode === "json-to-yaml" ? "yaml-to-json" : "json-to-yaml")
    }
  }

  const copyOutput = () => {
    navigator.clipboard.writeText(output)
    toast.success("Copied to clipboard")
  }

  const clearAll = () => {
    setInput("")
  }

  const loadSample = () => {
    if (mode === "json-to-yaml") {
      setInput(`{
  "name": "My Project",
  "version": "1.0.0",
  "description": "A sample configuration",
  "settings": {
    "debug": true,
    "timeout": 3000,
    "features": ["auth", "logging", "cache"]
  },
  "database": {
    "host": "localhost",
    "port": 5432,
    "name": "mydb"
  }
}`)
    } else {
      setInput(`name: My Project
version: "1.0.0"
description: A sample configuration
settings:
  debug: true
  timeout: 3000
  features:
    - auth
    - logging
    - cache
database:
  host: localhost
  port: 5432
  name: mydb`)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>JSON ↔ YAML Converter</CardTitle>
        <CardDescription>
          Convert between JSON and YAML formats instantly. Perfect for config files and API work.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Mode Selection */}
        <Tabs value={mode} onValueChange={(v) => setMode(v as ConversionMode)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="json-to-yaml">JSON → YAML</TabsTrigger>
            <TabsTrigger value="yaml-to-json">YAML → JSON</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Options */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label>Indent:</Label>
            <div className="flex gap-1">
              {[2, 4].map((size) => (
                <Button
                  key={size}
                  variant={indentSize === size ? "default" : "outline"}
                  size="sm"
                  onClick={() => setIndentSize(size)}
                >
                  {size} spaces
                </Button>
              ))}
            </div>
          </div>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={loadSample}>
            Load Sample
          </Button>
          <Button variant="outline" size="sm" onClick={clearAll}>
            <Trash2 className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>

        {/* Input/Output */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{mode === "json-to-yaml" ? "JSON Input" : "YAML Input"}</Label>
              <Badge variant="secondary">
                {mode === "json-to-yaml" ? "JSON" : "YAML"}
              </Badge>
            </div>
            <Textarea
              placeholder={
                mode === "json-to-yaml"
                  ? '{"key": "value"}'
                  : "key: value"
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="font-mono text-sm min-h-[300px]"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{mode === "json-to-yaml" ? "YAML Output" : "JSON Output"}</Label>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {mode === "json-to-yaml" ? "YAML" : "JSON"}
                </Badge>
                {output && (
                  <>
                    <Button variant="ghost" size="sm" onClick={swapContent}>
                      <ArrowRightLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={copyOutput}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
            <Textarea
              value={output}
              readOnly
              placeholder="Output will appear here..."
              className={`font-mono text-sm min-h-[300px] ${error ? "border-destructive" : ""}`}
            />
          </div>
        </div>

        {/* Status */}
        {error ? (
          <div className="flex items-center gap-2 text-destructive bg-destructive/10 p-3 rounded-lg">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        ) : output ? (
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/20 p-3 rounded-lg">
            <Check className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">Converted successfully</span>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
